import { type CVEditingSection, type FullCV } from '@cibli/shared';
import { Car, GraduationCap, Mail, MapPin, Navigation, Phone } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import EditableSection from '../../../components/form/EditableSection';

interface ContactInfoProps {
  cv: FullCV;
  accent: string;
  onEdit?: (section: CVEditingSection) => void;
}

export default function ContactInfo({ cv, accent, onEdit }: ContactInfoProps) {
  const { t } = useTranslation();
  const handleEdit = () => onEdit && onEdit('personalInfos');
  const driverLicenseLabel = useMemo(() => {
    if (!cv.hasDriverLicense) return '';
    const types = cv.driverLicenseTypes;
    const filled = types != null && types.trim().length > 0;
    const licenseKey = t('CV.drivingLicense');
    if (!filled) return licenseKey;
    const escaped = licenseKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const cleanTypes = types.trim().replace(new RegExp(`^${escaped}\\s*`, 'i'), '');
    return cleanTypes ? `${licenseKey} ${cleanTypes}` : licenseKey;
  }, [cv.hasDriverLicense, cv.driverLicenseTypes, t]);

  const jobTitleLabel = useMemo(() => {
    if (!cv.jobTitle) return null;
    return cv.jobTitle;
  }, [cv.jobTitle]);

  return (
    <EditableSection
      onEdit={handleEdit}
      editable={onEdit !== undefined}
      accentColor={accent}
      enablePageBreak={false}
    >
      <div className="rounded-lg border mb-2 p-2 m-2">
        <div className="space-y-2">
          {jobTitleLabel && (
            <p className="text-[11px] font-bold leading-relaxed text-black">{jobTitleLabel}</p>
          )}
          <div className="flex items-center gap-2">
            <Phone className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
            <span className="text-[11px] font-semibold leading-relaxed text-black">{cv.phone}</span>
          </div>
          {cv.email && (
            <div className="flex items-center gap-2 break-all">
              <Mail className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
              <span className="text-[11px] font-semibold leading-relaxed text-black">
                {cv.email}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <MapPin className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
            <span className="text-[11px] font-semibold leading-relaxed text-black">
              {cv.location}
            </span>
          </div>
          {cv.hasDriverLicense && (
            <div className="flex items-center gap-2">
              <Car className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
              <span className="text-[11px] font-semibold leading-relaxed text-black">
                {driverLicenseLabel}
              </span>
            </div>
          )}
          {cv.isMobile && (
            <div className="flex items-center gap-2">
              <Navigation className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
              <span className="text-[11px] font-semibold leading-relaxed text-black">
                {t('CV.mobile')}
              </span>
            </div>
          )}
          {cv.openToTraining && (
            <div className="flex items-center gap-2">
              <GraduationCap className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
              <span className="text-[11px] font-semibold leading-relaxed text-black">
                {t('Forms.PersonalInfo.openToTraining')}
              </span>
            </div>
          )}
        </div>
      </div>
    </EditableSection>
  );
}
